import { Alert, Platform, StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  DetailRow,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { usePurchases } from "~/hooks/usePurchases";
import { openExternalUrl } from "~/utils/external-link";
import { PRO_PRICE_TEXT } from "~/utils/plan-limits";
import { spacing } from "~/styles/theme";

const PRO_FEATURES = [
  "Unlimited collections",
  "Unlimited plants",
  "Upload your own garden maps and site plans",
  "Unlimited photos per plant",
];

export default function SubscriptionScreen() {
  const { user, refresh } = useAuth();
  const plan = usePlan();
  const purchases = usePurchases(user?.id ?? null);

  async function manage() {
    try {
      if (purchases.supportsCustomerCenter && Platform.OS !== "web") {
        await purchases.presentCustomerCenter();
        await refresh();
        return;
      }
      const url = await purchases.getManagementUrl();
      if (url) {
        await openExternalUrl(url);
        return;
      }
      Alert.alert(
        "Manage your subscription",
        "Use the App Store or Google Play subscription settings on the device you purchased with.",
      );
    } catch (error) {
      Alert.alert(
        "Unable to open subscription management",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  }

  return (
    <ProtectedScreen
      title={plan.isPro ? "Your subscription" : "Florafolio Pro"}
      subtitle={
        plan.isPro
          ? "Thanks for supporting Florafolio."
          : `Everything unlocked for ${PRO_PRICE_TEXT} a month.`
      }
    >
      <Card gap="md" accent="mint">
        <View style={stylesheet.header}>
          <SectionHeader title={plan.isPro ? "Pro" : "Free"} />
          <Badge tone={plan.isPro ? "success" : "neutral"}>
            {plan.isPro ? "Active" : "No subscription"}
          </Badge>
        </View>

        <View style={stylesheet.metrics}>
          <View style={stylesheet.metric}>
            <MetricCard
              label="Collections"
              value={
                plan.collections.limit === null
                  ? `${plan.collections.used}`
                  : `${plan.collections.used} / ${plan.collections.limit}`
              }
            />
          </View>
          <View style={stylesheet.metric}>
            <MetricCard
              label="Plants"
              value={
                plan.plants.limit === null
                  ? `${plan.plants.used}`
                  : `${plan.plants.used} / ${plan.plants.limit}`
              }
            />
          </View>
        </View>

        <Stack gap="xs">
          {PRO_FEATURES.map((feature) => (
            <DetailRow
              key={feature}
              label={feature}
              value={plan.isPro ? "Included" : "Pro"}
            />
          ))}
        </Stack>
      </Card>

      {plan.isPro ? (
        <Card gap="sm">
          <SectionHeader
            title="Manage"
            description="Change or cancel your plan through the store you purchased with."
          />
          <Button
            align="start"
            variant="outline"
            disabled={purchases.managing}
            onPress={() => void manage()}
          >
            <ButtonText>
              {purchases.managing ? "Opening..." : "Manage subscription"}
            </ButtonText>
          </Button>
        </Card>
      ) : (
        <UpgradePrompt />
      )}

      <Card gap="sm">
        <SectionHeader
          title="Free forever"
          description="One collection and ten plants, with full offline access and everything in the botanical index. Free forever — the 14-day trial is for Pro."
        />
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.m,
  },
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 160, flex: 1 },
});
